import React, { useContext, isValidElement, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { withErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { Typography, Card, CardMedia, SkeletonBlock } from '@one-thd/sui-atomic-components';
import { SingleProductSpecifications } from './SingleProductSpecifications';
import { changeSelectedItem, getDdo, getProductImage, triggerAnalyticsCustomEvent } from '../helpers';
import { dataModel } from '../dataModel';

export const MultiProductSpecificationsComponent = ({ itemIds, children }) => {
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const [selectedItemId, setSelectedItemId] = useState(itemIds[0]);

  useEffect(() => {
    setSelectedItemId(itemIds[0]);
  }, [itemIds]);

  const handleItemClick = (product) => {
    setSelectedItemId(product?.itemId);
    changeSelectedItem(product?.itemId);
    triggerAnalyticsCustomEvent('zone-b.click', getDdo(product, 'specifications-pipfigurator'));
  };

  const { data, loading } = useDataModel('products', { variables: { itemIds } });

  if (loading) {
    return (
      <div
        className="sui-w-full"
        data-component="MultiSpecificationsPlaceholder"
        data-testid="multi-specifications-placeholder"
      >
        <SkeletonBlock aspect="wide" height={52} width="100%" />
      </div>
    );
  }
  if (!data || !data.products || data.products.length === 0) {
    return null;
  }

  const products = data.products;
  const selectedProduct = products?.find((item) => item.itemId === selectedItemId);

  return (
    <div className="sui-max-w-full" data-component="ConfigSpecifications" data-testid="Config-specifications">
      <div className={`${isMobile ? 'sui-grid-flow-col' : 'sui-grid-cols-5'} sui-grid sui-grid-rows-1 sui-gap-4`}>
        {products?.map((product, index) => (
          <Card
            key={product.itemId}
            className={`${selectedItemId === product.itemId ? 'sui-border-2 sui-border-strongest' : 'sui-border-1'
              } sui-max-w-xs`}
            onClick={() => handleItemClick(product)}
          >
            <CardMedia
              data-testid={`Config-spec-button-${index}`}
              component="img"
              height={{ sm: '40px', md: '116px', lg: '253px' }}
              width={{ sm: '40px', md: '116px', lg: '253px' }}
              src={getProductImage(product?.media?.images)}
              alt={product?.title}
            />
          </Card>
        ))}
      </div>
      {selectedItemId ? (
        <div data-component="BodyConfigSpecifications">
          {selectedProduct && selectedProduct !== null ? (
            <div className="sui-grid sui-mt-4">
              <Typography variant="body-lg" weight="bold" data-testid="tile-product-title">
                {selectedProduct?.identifiers?.productLabel}
              </Typography>
            </div>
          ) : null}
          <div className="sui-mt-4" data-testid="specs-accordion-body">
            <SingleProductSpecifications
              itemId={selectedItemId}
            >
              {children}
            </SingleProductSpecifications>
          </div>
        </div>
      ) : null}
    </div>
  );
};

MultiProductSpecificationsComponent.displayName = 'MultiProductSpecifications';
MultiProductSpecificationsComponent.dataModel = dataModel;

const propTypes = {
  itemIds: PropTypes.arrayOf(PropTypes.string),
  children: (props, propName, componentName) => {
    const prop = props[propName];
    let error;
    if (prop && (!isValidElement(prop) || prop.type.name !== 'KeyProductFeatures')) {
      error = new Error(`'${componentName}' only accepts a single child of type 'KeyProductFeatures'`);
    }
    return error;
  },
};

const defaultProps = {
  itemIds: [],
  children: null,
};

MultiProductSpecificationsComponent.propTypes = propTypes;
MultiProductSpecificationsComponent.defaultProps = defaultProps;

export const MultiProductSpecifications = withErrorBoundary(MultiProductSpecificationsComponent);

MultiProductSpecifications.dataModel = dataModel;
MultiProductSpecifications.propTypes = propTypes;
MultiProductSpecifications.defaultProps = defaultProps;
